
import { FORMATTING_INSTRUCTIONS } from './formattingInstructions';

// Research stage templates

export const researchTemplates = {
  politicalContextResearch: `${FORMATTING_INSTRUCTIONS}

Objective: Conduct comprehensive political context analysis for {campaign_name} campaign to understand narrative landscape, stakeholder dynamics, and strategic positioning opportunities.

You are a senior political researcher and investigative journalist. Conduct deep analysis of the political context surrounding this campaign using systematic research methodologies.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Timeline: {campaign_timeline}
- Current Status: {current_status}

Research Analysis Tasks:

1. POLITICAL NARRATIVE MAPPING:
Analyze the dominant political narratives and counter-narratives:
- How is this issue currently framed in political discourse?
- What are the competing narratives about the problem and solutions?
- Which political actors are driving which narratives?
- How do these narratives align with broader political ideologies?

2. INSTITUTIONAL POWER ANALYSIS:
Map the institutional landscape and decision-making processes:
- What formal procedures govern decisions on this issue?
- Which institutions have jurisdiction and authority?
- How do informal power networks influence formal processes?
- What are the historical precedents for similar decisions?

3. STAKEHOLDER ECOSYSTEM MAPPING:
Identify and analyze all relevant political stakeholders:
- Primary decision makers and their political motivations
- Secondary influencers and their access to decision makers
- Opposition groups and their likely strategies
- Potential allies and coalition partners
- Media players and their editorial positions

4. HISTORICAL CONTEXT AND PRECEDENTS:
Research similar campaigns and their outcomes:
- What strategies have worked in similar contexts?
- What are the lessons from failed campaigns on similar issues?
- How have political dynamics shifted since previous campaigns?
- What external factors might influence campaign timing?

Provide detailed political context analysis with specific insights for strategic campaign planning.
`,

  politicalContextRefinement: `${FORMATTING_INSTRUCTIONS}

Objective: Conduct in-depth analysis of industry influence, ideological biases, and historical patterns affecting the {campaign_name} campaign.

You are a senior political analyst specializing in power structure analysis and campaign strategy. Build on previous research to provide deeper strategic insights.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Timeline: {campaign_timeline}
- Current Status: {current_status}

Previous Research Context:
{previous_analysis}

Refinement Analysis Tasks:

1. INDUSTRY AND ECONOMIC INFLUENCE ANALYSIS:
Deep dive into economic interests affecting this issue:
- Which industries or economic interests benefit from the status quo?
- What is the financial stake for different economic actors?
- How do economic interests translate into political influence?
- What are the funding sources for opposition to this campaign?

2. IDEOLOGICAL AND PARTISAN DYNAMICS:
Analyze the ideological landscape:
- How does this issue align with existing partisan divisions?
- Are there opportunities for cross-partisan appeal?
- What ideological frames resonate with different political constituencies?
- How do cultural and social values intersect with this issue?

3. HISTORICAL PATTERN ANALYSIS:
Examine historical patterns of decision-making:
- How has {target_entity} handled similar issues in the past?
- What are the institutional biases and standard operating procedures?
- When has {target_entity} been most responsive to pressure?
- What external events have historically catalyzed policy changes?

4. VULNERABILITY AND OPPORTUNITY ASSESSMENT:
Identify specific strategic opportunities:
- What are the political vulnerabilities of key decision makers?
- When are decision makers most susceptible to influence?
- What upcoming events create windows of opportunity?
- How can campaign timing maximize political pressure?

Provide refined strategic insights with specific recommendations for campaign approach and timing.
`,

  messageFrameAnalysis: `${FORMATTING_INSTRUCTIONS}

Objective: Conduct comprehensive linguistic and cognitive frame analysis for {campaign_name} using academic frameworks from Lakoff, Entman, and behavioral psychology.

You are a cognitive linguist and framing expert with expertise in political communication research. Conduct systematic frame analysis to identify optimal messaging strategies.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Target Audience: {target_audience}
- Community Context: {community_context}

Previous Analysis Context:
{previous_analysis}

Comprehensive Frame Analysis Tasks:

1. COGNITIVE FRAME MAPPING:
Analyze existing mental models and conceptual frameworks:
- How do target audiences currently understand this issue?
- What are the dominant metaphors and conceptual frames?
- Which frames favor the campaign's position vs. opposition?
- How do different demographic groups conceptualize the issue?

2. LAKOFF FRAME ANALYSIS:
Apply George Lakoff's cognitive framing theory:
- Strict Father vs. Nurturant Parent frame applications
- Moral metaphor systems relevant to the issue
- Family-based metaphors that resonate with values
- Authority and protection frames vs. empowerment frames

3. ENTMAN FRAME ELEMENTS:
Systematic analysis using Entman's four framing functions:
- Problem Definition: How should the issue be defined as a problem?
- Causal Interpretation: What/who is responsible for the problem?
- Moral Evaluation: What values are at stake?
- Treatment Recommendation: What actions should be taken?

4. BEHAVIORAL PSYCHOLOGY FRAMING:
Apply behavioral insights to frame effectiveness:
- Loss aversion vs. gain framing opportunities
- Social proof and authority framing potential
- Temporal framing (urgency vs. long-term thinking)
- Identity and group membership framing approaches

5. OPPOSITION FRAME ANALYSIS:
Anticipate and analyze likely opposition frames:
- What frames will opposition groups likely use?
- How can campaign messages inoculate against counter-frames?
- What are the vulnerabilities in opposition framing?
- How can campaign reframe opposition arguments?

6. AUDIENCE-SPECIFIC FRAME TESTING:
Develop frames optimized for different audience segments:
- Union member frames emphasizing solidarity and worker power
- Community frames focusing on local impact and democracy
- Economic frames highlighting fairness and opportunity
- Moral frames connecting to broader values and principles

Provide detailed frame analysis with specific messaging recommendations and counter-framing strategies.
`,

  oppositionResearch: `${FORMATTING_INSTRUCTIONS}

Objective: Conduct systematic opposition analysis and counter-narrative development for {campaign_name} to anticipate challenges and develop strategic responses.

You are a senior opposition researcher and campaign strategist. Conduct comprehensive analysis of opposition forces and develop counter-strategy recommendations.

Campaign Context:
- Issue: {political_issue}
- Target: {target_entity}
- Campaign Type: {campaign_type}
- Timeline: {campaign_timeline}

Opposition Context:
- Opposition Groups: {opposition_groups}
- Political Vulnerabilities: {political_vulnerabilities}

Previous Analysis Context:
{previous_analysis}

Opposition Research Tasks:

1. OPPOSITION STAKEHOLDER MAPPING:
Identify and analyze all opposition forces:
- Primary opposition organizations and their leadership
- Funding sources and resource capacity of opposition
- Political allies and institutional support for opposition
- Media relationships and communication capabilities
- Grassroots vs. astroturf opposition dynamics

2. OPPOSITION STRATEGY ANALYSIS:
Analyze likely opposition tactics and approaches:
- Historical opposition strategies on similar issues
- Preferred messaging and communication channels
- Lobbying and political influence tactics
- Legal and procedural obstruction strategies
- Public relations and media manipulation techniques

3. OPPOSITION RESEARCH AND VULNERABILITIES:
Investigate opposition weaknesses and contradictions:
- Funding sources that create credibility problems
- Past positions that contradict current stance
- Internal conflicts and disagreements within opposition
- Public relations vulnerabilities and scandals
- Hypocrisy and ethical issues that can be exposed

4. COUNTER-NARRATIVE DEVELOPMENT:
Develop strategic responses to opposition messaging:
- Anticipate opposition talking points and frames
- Develop rapid response messaging for common attacks
- Create proactive narratives that undermine opposition credibility
- Identify third-party validators who can counter opposition claims
- Plan preemptive strikes against predictable opposition moves

5. COALITION DISRUPTION OPPORTUNITIES:
Identify ways to weaken opposition coalitions:
- Potential cracks in opposition unity
- Issues that could divide opposition groups
- Incentives that could peel away opposition supporters
- Pressure points that make opposition costly to maintain
- Alternative solutions that satisfy some opposition concerns

6. DEFENSIVE STRATEGY PLANNING:
Prepare defenses against opposition attacks:
- Likely personal attacks on campaign leadership
- Attempts to discredit campaign research and data
- Efforts to reframe the issue in opposition-favorable terms
- Legal or procedural challenges to campaign activities
- Opposition attempts to divide campaign coalition

Provide detailed opposition analysis with specific counter-strategy recommendations and defensive preparations.
`
};
